// Constants for the in-meeting Agenda tab.
import { THEME_COLOR } from "../../../styles/colors";

// Lifecycle state of an agenda item.
export const AGENDA_STATE = {
  COMPLETED: "completed",
  IN_PROGRESS: "in-progress",
  UPCOMING: "upcoming",
};

// Maps agenda state → ProgressTrackerStep `type` (marker + track visuals).
export const AGENDA_STATE_TO_STEP_TYPE = {
  [AGENDA_STATE.COMPLETED]: "past",
  [AGENDA_STATE.IN_PROGRESS]: "present",
  [AGENDA_STATE.UPCOMING]: "future",
};

// ---- Display text ----
export const ADD_AGENDA_LABEL = "Add agenda";
export const IN_PROGRESS_LABEL = "In progress";
export const RECAP_HEADING = "Recap";
export const NO_RECAP_HEADING = "No recap";
export const LOAD_ERROR_MESSAGE = "Unable to load agenda";
export const EMPTY_AGENDA_MESSAGE = "Add your agenda items, and we'll track your meeting progress in real time.";

// ---- Layout / sizing ----
// Fixed width of the right-hand action slot, shared across row states.
export const ACTION_SLOT_WIDTH = "72px";
// Title is capped at this many characters in the list; the full text is on hover. Separate from
// Action Items' cap because the tracker rail leaves this column less width.
export const TITLE_TRUNCATE_LENGTH = 60;
// Fixed, not "fit": a completed row's slot holds one chevron and every other row holds two
// buttons, so fitting to content gave completed titles more width and wrapped them differently.
export const LABEL_ROW_WIDTHS = ["fill", ACTION_SLOT_WIDTH];
// b200 line-height; controls center within this.
export const ROW_LINE_HEIGHT = "16px";
// Meridian small input height — inline rows reserve this to avoid layout shifts.
export const EDITABLE_ROW_HEIGHT = "32px";

// Horizontal inset for every row state (display, delete-confirm, edit/add), owned by the Agenda
// container and passed down so all states stay aligned from one place.
export const ROW_SPACING_INSET = "none 300";

// Gap above and below the recap card; replaces the step spacer it displaces. Left inset matches
// ROW_SPACING_INSET so the card lines up with the row title, right keeps it clear of the chevron.
export const RECAP_WRAPPER_SPACING_INSET = "200 300 300 300";

// Padding inside the recap card: a little tighter vertically than horizontally.
export const RECAP_CARD_SPACING_INSET = "350 400";

// Vertical breathing room inside the tracker's overflow:hidden clip. The clip is needed because
// ProgressTracker's internal flex sizing overflows its box; this padding keeps the edit/add row's
// negative margins (INPUT_MARKER_OFFSET) from being clipped at the first and last rows.
export const TRACKER_CLIP_PADDING = 10;

// Vertical gap below each settled row, reserved so a single-line settled row matches the height of
// an inline edit row (EDITABLE_ROW_HEIGHT - ROW_LINE_HEIGHT). Constant for every step — toggling it
// changed the scroll container's content height and visibly shifted the list.
export const STEP_GAP_SPACER_SIZE = 16;

// How far an inline edit/add input extends above and below its layout slot, so the tracker marker
// stays centered on the taller input without the input growing the row. Half the difference between
// EDITABLE_ROW_HEIGHT (32) and ROW_LINE_HEIGHT (16).
export const INPUT_MARKER_OFFSET = 8;

// ---- Theme tokens ----
// Title of the in-progress row: teal text with the line-height pinned to the row so the title
// stays centered on its tracker marker.
export const IN_PROGRESS_TITLE_TOKENS = {
  textColorPrimary: THEME_COLOR.DEEP_TEAL,
  textB200LineHeight: ROW_LINE_HEIGHT,
};

// Progress-tracker styling applied at the Agenda root (compact → Small tokens).
export const AGENDA_TRACKER_TOKENS = {
  progressTrackerTrackWidthSmall: 3,
  progressTrackerTrackLengthMinimumSmall: 12,
  // 16px markers match the check-knockout icon; b200 line-height centers titles on markers.
  progressTrackerMilestoneDiameterSmall: 16,
  textB200LineHeight: "16px",
  progressTrackerMilestoneForegroundColorThemePresentDefault: THEME_COLOR.TEAL_GREEN,
  progressTrackerMilestoneBorderColorThemePresentDefault: THEME_COLOR.TEAL_GREEN,
  progressTrackerMilestoneForegroundColorThemePastDefault: THEME_COLOR.TEAL_GREEN,
  // Not-started markers and connectors are gray; drop the 1px track border.
  progressTrackerMilestoneBorderColorThemeFutureDefault: THEME_COLOR.PALE_GRAY,
  progressTrackerTrackBackgroundColorIncompleteTheme: THEME_COLOR.PALE_GRAY,
  progressTrackerTrackBorderWidth: 0,
  inputBorderColorDefault: THEME_COLOR.TEAL_GREEN,
  // Tighten per-step spacing so the gap comes from the reserved row height, not extra padding.
  progressTrackerSpacingSmall: 2,
};

// Radius 12 is the shared card treatment (SectionCard, CollapsibleCard, MetricCard, StatusTable).
export const RECAP_CARD_TOKENS = {
  boxBorderColorOutline: THEME_COLOR.MIST_GRAY,
  boxBorderRadiusOutline: 12,
};
